/** @jsx jsx */
import React, { useState, type ReactNode, useRef } from 'react';
import { css, jsx } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import WarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import mergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import MoveIssuesBetweenVersions from '@atlassian/jira-software-releases-move-issues-between-versions/src/async.tsx';
import { useMoveIssuesBetweenVersionsQuery } from '@atlassian/jira-software-releases-move-issues-between-versions/src/ui/index.tsx';
import { ISSUE_CARD_DATA_COMPONENT_SELECTOR } from '../../../../common/constants.tsx';
import type { moreButton_softwareReleasesVersionDetailIssueList_jiraVersion$key } from './__generated__/moreButton_softwareReleasesVersionDetailIssueList_jiraVersion.graphql';
import type { moreButton_softwareReleasesVersionDetailIssueList_jiraIssue$key } from './__generated__/moreButton_softwareReleasesVersionDetailIssueList_jiraIssue.graphql';
import messages from './messages.tsx';

type Props = {
	jiraIssue: moreButton_softwareReleasesVersionDetailIssueList_jiraIssue$key;
	jiraVersion: moreButton_softwareReleasesVersionDetailIssueList_jiraVersion$key;
	hasWarnings: Boolean;
	hasOpenPullRequestWarning?: Boolean;
	hasFailingBuildsWarning?: Boolean;
	hasUnreviewedCodeWarning?: Boolean;
	hasOpenReviewWarning?: Boolean;
};

export const MoreButton = ({
	jiraIssue,
	jiraVersion,
	hasWarnings,
	hasOpenPullRequestWarning,
	hasFailingBuildsWarning,
	hasUnreviewedCodeWarning,
	hasOpenReviewWarning,
}: Props): JSX.Element => {
	const issue = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment moreButton_softwareReleasesVersionDetailIssueList_jiraIssue on JiraIssue {
				...ui_softwareReleasesMoveIssuesBetweenVersions_MoveIssuesBetweenVersions_jiraIssue
				key
			}
		`,
		jiraIssue,
	);

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment moreButton_softwareReleasesVersionDetailIssueList_jiraVersion on JiraVersion {
				id @required(action: THROW)
				canAddAndRemoveIssues
				project @required(action: THROW) {
					id @required(action: THROW)
				}
			}
		`,
		jiraVersion,
	);

	const [isPopupOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	const [moveIssuesBetweenVersionsQueryReference, loadMoveIssuesBetweenVersionsQuery] =
		useMoveIssuesBetweenVersionsQuery(version.project.id, 4, version.id);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const moreButtonRef = useRef<HTMLElement | null>(null);

	const togglePopup = () => {
		if (hasWarnings) {
			const warnings = [];
			if (hasOpenPullRequestWarning) {
				warnings.push('OPEN_PULL_REQUEST');
			}
			if (hasFailingBuildsWarning) {
				warnings.push('FAILING_BUILD');
			}
			if (hasUnreviewedCodeWarning) {
				warnings.push('UNREVIEWED_CODE');
			}
			if (hasOpenReviewWarning) {
				warnings.push('OPEN_REVIEW');
			}

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'clicked',
					actionSubject: 'IssueWarningMoreButton',
				}),
				{
					warnings: warnings.toString(),
				},
			);
		}

		if (moveIssuesBetweenVersionsQueryReference == null) {
			loadMoveIssuesBetweenVersionsQuery();
		}
		setIsOpen(!isPopupOpen);
	};

	const handlePopupClose = () => {
		setIsOpen(false);
		if (moreButtonRef.current) {
			requestAnimationFrame(() => {
				moreButtonRef.current?.focus();
			});
		}
	};

	return (
		<OnHoverVisibility
			{...(fg('jfp_a11y_issue_view_release_move_focus_to_trigger') && {
				isPopupVisible: isPopupOpen,
			})}
		>
			{(!!version.canAddAndRemoveIssues || hasWarnings) && (
				<MoveIssuesBetweenVersions
					renderTrigger={({ triggerProps }) => (
						<Button
							{...triggerProps}
							ref={mergeRefs(triggerProps.ref, moreButtonRef)}
							iconBefore={
								hasWarnings ? (
									<WarningIcon
										testId="software-releases-version-detail-issue-list.ui.issues.issue-card.more-button.issue-card-warning-more-button"
										color={token('color.icon.warning')}
										label={formatMessage(messages.moreVersions)}
										spacing="spacious"
									/>
								) : (
									<MoreIcon
										spacing="spacious"
										label={formatMessage(messages.moreVersions, { issueKey: issue.key })}
									/>
								)
							}
							onClick={togglePopup}
							onMouseOver={loadMoveIssuesBetweenVersionsQuery}
							appearance="subtle"
							isSelected={isPopupOpen}
						/>
					)}
					queryReference={moveIssuesBetweenVersionsQueryReference}
					jiraIssue={issue}
					onClose={handlePopupClose}
					isOpen={isPopupOpen}
				/>
			)}
		</OnHoverVisibility>
	);
};

export const OnHoverVisibility = ({
	children,
	isPopupVisible,
}: {
	children: ReactNode;
	isPopupVisible?: boolean;
}): JSX.Element => (
	<div css={[styles, stylesWithFocusWithin, isPopupVisible && stylesWithVisibility]}>
		{children}
	</div>
);

const styles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > *': {
		visibility: 'hidden',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`[data-component-selector="${ISSUE_CARD_DATA_COMPONENT_SELECTOR}"]:hover & > *`]: {
		visibility: 'visible',
	},
	width: '40px',
});
const stylesWithFocusWithin = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& button': {
		transition: 'visibility 0.01s',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`[data-component-selector="${ISSUE_CARD_DATA_COMPONENT_SELECTOR}"]:focus-within & > *`]: {
		visibility: 'visible',
	},
});
const stylesWithVisibility = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > *': {
		visibility: 'visible',
	},
});
